/**
 * Thin API client. Relative URLs only — the SPA is served from the same origin
 * as the API, so there is no CORS configuration to get wrong and no API base
 * URL to leak into a build artifact.
 *
 * Every non-2xx response is an RFC 7807 problem-details document from the
 * backend, so errors carry a human-readable title and detail rather than an
 * opaque status code.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly title: string,
    public readonly detail: string,
    public readonly extras?: Record<string, unknown>,
  ) {
    super(`${title}: ${detail}`);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { title: 'Unexpected response', detail: text.slice(0, 300) };
    }
  }

  if (!res.ok) {
    const p = (parsed ?? {}) as { title?: string; detail?: string };
    throw new ApiError(
      res.status,
      p.title ?? `HTTP ${res.status}`,
      p.detail ?? 'The request failed.',
      p as Record<string, unknown>,
    );
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/**
 * Like api.get, but returns the status alongside the body instead of throwing
 * on 4xx/5xx.
 *
 * Some endpoints report a failure condition *as their payload* — /healthz
 * answers 503 with a full body describing exactly what is unhealthy, and a
 * station with no shadow report yet answers 404. Those pages must render the
 * condition, not swallow it into a generic error box.
 */
export async function getStatus<T>(path: string): Promise<{ status: number; body: T | null }> {
  const res = await fetch(path, { credentials: 'same-origin' });
  const text = await res.text();
  let parsed: T | null = null;
  if (text) {
    try {
      parsed = JSON.parse(text) as T;
    } catch {
      parsed = null;
    }
  }
  return { status: res.status, body: parsed };
}

/** Build a query string, dropping undefined values. */
export function qs(params: Record<string, string | number | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}
